import * as React from 'react';

import type {
  CareerRole_MerchandisingJobCategory as CareerRoleCategory,
  CareerRole_MerchandisingJobCategory as JobCategory,
  CareerRole_PathwayLevel as ProficiencyLevel,
} from '__generated__/graphql-types';

import { useParams } from 'js/lib';

import RelatedRolesComponent from 'bundles/browse-common/components/RelatedRolesComponent';
import { useDomainEntity } from 'bundles/browse-common/contexts/DomainEntityContext';
import type { KnownDomainSlugs } from 'bundles/browse-common/types/domain';
import { isKnownDomainSlug } from 'bundles/browse-common/utils/domain-utils';
import { toJobCategory } from 'bundles/browse-common/utils/toJobCategory';
import type { PageConfig_ComponentConfig_PageConfig_ExploreRolesComponent_Fragment as ExploreRolesComponentPageConfig } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { useMediumIntentContext } from 'bundles/search-common/providers/MediumIntentProvider';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useCareerRolesByDomain from 'bundles/unified-common/hooks/useCareerRolesByDomain';
import useCareerRolesCollection, {
  normalizeCareerRolesCollection,
} from 'bundles/unified-common/hooks/useCareerRolesCollection';
import type { CareerRolesCollectionQueryQuery as CareerRolesCollectionQueryType } from 'bundles/unified-common/queries/__generated__/CareerRolesCollection';
import type { CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';
import { generateCacheKey, getChipFilters, getDropdownMenuProps } from 'bundles/unified-common/utils/pathwaysFilters';

import _t from 'i18n!nls/browse-common';

const knownRolelessDomains = [
  'health',
  'math-and-logic',
  'language-learning',
  'personal-development',
  'physical-science-and-engineering',
  'social-sciences',
] as const satisfies KnownDomainSlugs[];

type Props = {
  pageConfigComponent: ExploreRolesComponentPageConfig;
};

function useLoadExploreRolesData() {
  const { domain } = useDomainEntity() ?? {};
  const isConsumerSerp = useIsConsumerSerp();

  const chipFilters = getChipFilters();
  const dropdownMenuProps = getDropdownMenuProps();
  const [currentSelectedDropdownValue, setSelectedDropdownValue] = React.useState<ProficiencyLevel>(
    dropdownMenuProps?.defaultValue
  );
  const [currentChipFilterIndex, setCurrentChipFilterIndex] = React.useState(0);

  const viewedCombinations = React.useRef<Map<string, CareerRolesCollectionType | undefined>>(new Map());
  const combinationKey = generateCacheKey(currentSelectedDropdownValue, currentChipFilterIndex);
  const cachedContent = viewedCombinations.current.get(combinationKey);
  const skipQuery = viewedCombinations.current.has(combinationKey);

  const addToLocalCache = (data: CareerRolesCollectionQueryType) => {
    if (
      data.PathwaySkillCareerRoles?.queryRolesByPathwayLevelAndCategory !== undefined &&
      !viewedCombinations.current.has(combinationKey)
    ) {
      const normalizedData = normalizeCareerRolesCollection(data);

      if (normalizedData?.entities) {
        normalizedData.entities = normalizedData.entities.filter((e) =>
          domain?.slug ? e.domain?.id === domain?.slug : true
        );
      }

      viewedCombinations.current.set(combinationKey, normalizedData);
    }
  };

  const jobCategory = (): JobCategory | undefined => {
    if (isKnownDomainSlug(domain?.slug)) {
      return toJobCategory(domain.slug);
    }
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return currentChipFilterIndex === 0 ? undefined : (chipFilters[currentChipFilterIndex].id as CareerRoleCategory);
  };

  const shouldUseDomainQuery = !!domain || isConsumerSerp;
  // skip this query if it's a domain page or consumer SERP
  const { data: fetchedContent, loading: isCollectionLoading } = useCareerRolesCollection({
    ssr: false,
    skip: skipQuery || shouldUseDomainQuery,
    level: currentSelectedDropdownValue,
    limit: domain ? 10 : 2,
    jobCategory: jobCategory(),
    onCompleted: (data) => {
      addToLocalCache(data);
    },
  });
  const { mediumIntentData } = useMediumIntentContext();
  const domainId = mediumIntentData?.matchedEntity.replace(/\s+/g, '-').toLowerCase();
  const { data: fetchedContentByDomain, loading: isCollectionLoadingByDomain } = useCareerRolesByDomain({
    ssr: false,
    skip: skipQuery || !shouldUseDomainQuery || (!domain?.slug && !isConsumerSerp),
    domainId: domain?.slug || domainId || '',
  });

  return {
    setSelectedDropdownValue,
    setCurrentChipFilterIndex,
    isCollectionLoading: !cachedContent && (shouldUseDomainQuery ? isCollectionLoadingByDomain : isCollectionLoading),
    collection: cachedContent || (shouldUseDomainQuery ? fetchedContentByDomain : fetchedContent),
    chipFilters,
    dropdownMenuProps,
    currentChipFilterIndex,
    currentSelectedDropdownValue,
    domain,
  };
}

const ExploreRolesRenderer = (props: Props) => {
  const {
    pageConfigComponent: { hideFilters = false },
  } = props;

  const { domainSlug } = useParams();

  const {
    setSelectedDropdownValue,
    setCurrentChipFilterIndex,
    isCollectionLoading,
    collection,
    chipFilters,
    dropdownMenuProps,
    currentChipFilterIndex,
    currentSelectedDropdownValue,
    domain,
  } = useLoadExploreRolesData();

  if (domainSlug && knownRolelessDomains.includes(domainSlug)) {
    return null;
  }

  const labels = domain
    ? {
        headline: _t('Explore roles'),
        description: _t('Gain the knowledge and skills you need to advance.'),
      }
    : {
        headline: _t('Explore roles'),
        description: _t('Grow in your career and get new skills with these premium courses on a 7-day free trial'),
      };

  return (
    <RelatedRolesComponent
      setSelectedDropdownValue={setSelectedDropdownValue}
      setCurrentChipFilterIndex={setCurrentChipFilterIndex}
      isCollectionLoading={isCollectionLoading}
      collection={collection}
      chipFilters={chipFilters}
      dropdownMenuProps={dropdownMenuProps}
      currentChipFilterIndex={currentChipFilterIndex}
      currentSelectedDropdownValue={currentSelectedDropdownValue}
      hideFilters={hideFilters ?? false}
      {...labels}
    />
  );
};

export default ExploreRolesRenderer;
